import React, { useEffect, useState } from 'react';
import {
  Box,
  TextField,
  Button,
  Paper,
  Typography,
  FormControlLabel,
  Switch,
  Grid,
} from '@mui/material';
import {
  Save as SaveIcon,
  Clear as ClearIcon,
  ArrowBack as ArrowBackIcon,
} from '@mui/icons-material';
import { useNavigate, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { funcionarioService, FuncionarioCommand, Funcionario } from '../../services/funcionario';
import SelectCargo from '../cargo/SelectCargo';
import DatePickerField from '../common/DatePickerField';

interface FuncionarioFormProps {
  isEdit?: boolean;
}

const FuncionarioForm: React.FC<FuncionarioFormProps> = ({ isEdit = false }) => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const queryClient = useQueryClient();
  
  const [formData, setFormData] = useState<FuncionarioCommand>({
    id: undefined,
    codPessoa: 0,
    codCargo: 0,
    ativo: 1,
    dataContratacao: '',
    dataRecisao: null,
    cnpj: '',
    codEndereco: 0,
    cpf: '',
    dataNasc: '',
    email: '',
    nome: '',
    rg: '',
    telefone1: '',
    telefone2: '',
    bairro: '',
    cep: '',
    cidade: '',
    estado: '',
    logradouro: '',
    numero: '',
  });

  const [errors, setErrors] = useState({
    nome: '',
    cpf: '',
    email: '',
    dataNasc: '',
    dataContratacao: '',
    codCargo: '',
    telefone1: '',
    rg: '',
  });

  // Buscar funcionário para edição
  const { data: funcionario, isLoading } = useQuery({
    queryKey: ['funcionario', id],
    queryFn: () => funcionarioService.getById(Number(id)),
    enabled: isEdit && !!id,
  });

  useEffect(() => {
    if (funcionario && isEdit) {
      // Função para formatar data para dd/mm/yyyy (formato do DatePickerField)
      const formatarData = (data: string | null | undefined): string => {
        if (!data) return '';
        
        // Se já estiver em formato brasileiro (DD/MM/YYYY ou D/M/YYYY)
        if (data.includes('/')) {
          const partes = data.split('/');
          if (partes.length === 3) {
            const dia = partes[0].padStart(2, '0');
            const mes = partes[1].padStart(2, '0');
            const ano = partes[2];
            
            // Validar se o ano tem 4 dígitos
            if (ano.length === 4) {
              return `${dia}/${mes}/${ano}`;
            }
          }
        }
        
        // Se estiver em formato ISO (YYYY-MM-DD ou YYYY-MM-DDTHH:mm:ss)
        if (data.includes('T') || data.includes('-')) {
          const dataObj = new Date(data);
          if (!isNaN(dataObj.getTime())) {
            const dia = dataObj.getDate().toString().padStart(2, '0');
            const mes = (dataObj.getMonth() + 1).toString().padStart(2, '0');
            const ano = dataObj.getFullYear();
            return `${dia}/${mes}/${ano}`;
          }
        }
        
        return data;
      };

      setFormData({
        id: funcionario.id,
        codPessoa: funcionario.codPessoa,
        codCargo: funcionario.codCargo,
        ativo: funcionario.ativo,
        dataContratacao: formatarData(funcionario.dataContratacao),
        dataRecisao: formatarData(funcionario.dataRecisao),
        cnpj: funcionario.cnpj,
        codEndereco: funcionario.codEndereco,
        cpf: funcionario.cpf,
        dataNasc: formatarData(funcionario.dataNasc),
        email: funcionario.email,
        nome: funcionario.nome,
        rg: funcionario.rg,
        telefone1: funcionario.telefone1,
        telefone2: funcionario.telefone2,
        bairro: funcionario.bairro,
        cep: funcionario.cep,
        cidade: funcionario.cidade,
        estado: funcionario.estado,
        logradouro: funcionario.logradouro,
        numero: funcionario.numero,
      });
    }
  }, [funcionario, isEdit]);

  const handleInputChange = (field: keyof FuncionarioCommand) => (event: any) => {
    const value = event.target.value;
    setFormData(prev => ({ ...prev, [field]: value }));
    
    // Limpar erro do campo
    if (errors[field as keyof typeof errors]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  const handleCargoChange = (value: number) => {
    setFormData(prev => ({ ...prev, codCargo: value }));
    if (errors.codCargo) {
      setErrors(prev => ({ ...prev, codCargo: '' }));
    }
  };

  const handleAtivoChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const ativo = event.target.checked ? 1 : 0;
    setFormData(prev => ({ ...prev, ativo }));
  };

  const handleDateChange = (field: keyof FuncionarioCommand) => (value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }));

    // Limpar erro do campo
    if (errors[field as keyof typeof errors]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  const validateForm = () => {
    const newErrors = {
      nome: '',
      cpf: '',
      email: '',
      dataNasc: '',
      dataContratacao: '',
      codCargo: '',
      telefone1: '',
      rg: '',
    };
    let isValid = true;

    // Validação Nome
    if (!formData.nome.trim()) {
      newErrors.nome = 'Nome é obrigatório';
      isValid = false;
    } else if (formData.nome.length > 80) {
      newErrors.nome = 'Nome não pode ter mais de 80 caracteres';
      isValid = false;
    }

    // Validação CPF
    if (!formData.cpf.trim()) {
      newErrors.cpf = 'CPF é obrigatório';
      isValid = false;
    } else if (!/^\d{11}$/.test(formData.cpf.replace(/\D/g, ''))) {
      newErrors.cpf = 'CPF inválido';
      isValid = false;
    }

    // Validação Email
    if (!formData.email.trim()) {
      newErrors.email = 'E-mail é obrigatório';
      isValid = false;
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = 'E-mail inválido';
      isValid = false;
    }

    // Validação Data Nascimento
    if (!formData.dataNasc) {
      newErrors.dataNasc = 'Data de nascimento é obrigatória';
      isValid = false;
    }

    // Validação Data Contratação
    if (!formData.dataContratacao) {
      newErrors.dataContratacao = 'Data de contratação é obrigatória';
      isValid = false;
    }

    // Validação Cargo
    if (!formData.codCargo) {
      newErrors.codCargo = 'Cargo é obrigatório';
      isValid = false;
    }

    // Validação Telefone
    if (!formData.telefone1.trim()) {
      newErrors.telefone1 = 'Telefone é obrigatório';
      isValid = false;
    }

    // Validação RG
    if (!formData.rg.trim()) {
      newErrors.rg = 'RG é obrigatório';
      isValid = false;
    }

    setErrors(newErrors);
    return isValid;
  };

  // Função para converter data do formato brasileiro (dd/MM/yyyy) para ISO (yyyy-MM-dd)
  const converterDataParaISO = (data: string | null | undefined): string | null => {
    if (!data) return null;

    // Se já estiver no formato ISO, retorna como está
    if (data.includes('-') && data.split('-')[0].length === 4) {
      return data;
    }

    // Converte de dd/MM/yyyy para yyyy-MM-dd
    const partes = data.split('/');
    if (partes.length === 3) {
      const dia = partes[0].padStart(2, '0');
      const mes = partes[1].padStart(2, '0');
      const ano = partes[2];
      if (ano.length === 4) {
        return `${ano}-${mes}-${dia}`;
      }
    }

    return null;
  };

  const handleSubmit = (event: React.FormEvent) => {
    event.preventDefault();

    if (validateForm()) {
      const dataToSave = {
        ...formData,
        id: isEdit ? Number(id) : undefined,
        dataNasc: converterDataParaISO(formData.dataNasc) || '',
        dataContratacao: converterDataParaISO(formData.dataContratacao) || '',
        dataRecisao: converterDataParaISO(formData.dataRecisao),
      };

      saveMutation.mutate(dataToSave);
    }
  };

  const handleClear = () => {
    setFormData({
      id: undefined,
      codPessoa: 0,
      codCargo: 0,
      ativo: 1,
      dataContratacao: '',
      dataRecisao: null,
      cnpj: '',
      codEndereco: 0,
      cpf: '',
      dataNasc: '',
      email: '',
      nome: '',
      rg: '',
      telefone1: '',
      telefone2: '',
      bairro: '',
      cep: '',
      cidade: '',
      estado: '',
      logradouro: '',
      numero: '',
    });
    setErrors({
      nome: '',
      cpf: '',
      email: '',
      dataNasc: '',
      dataContratacao: '',
      codCargo: '',
      telefone1: '',
      rg: '',
    });
  };

  const handleBack = () => {
    navigate('/funcionarios');
  };

  // Mutation para salvar
  const saveMutation = useMutation({
    mutationFn: (data: FuncionarioCommand) => funcionarioService.save(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['funcionarios'] });
      navigate('/funcionarios');
    },
    onError: (error: any) => {
      console.error('Erro ao salvar funcionário:', error);
    },
  });

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
        <Typography>Carregando...</Typography>
      </Box>
    );
  }

  return (
    <Paper sx={{ p: 3 }}>
      <Box component="form" onSubmit={handleSubmit}>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
          {/* Dados Pessoais */}
          <Typography variant="h6" sx={{ fontWeight: 'bold', color: 'primary.main' }}>
            Dados Pessoais
          </Typography>
          
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 3 }}>
            <TextField
              fullWidth
              label="Nome do Funcionário"
              value={formData.nome}
              onChange={handleInputChange('nome')}
              error={!!errors.nome}
              helperText={errors.nome}
              required
              slotProps={{
                htmlInput: { maxLength: 80 }
              }}
            />
            
            <TextField
              fullWidth
              label="E-mail"
              type="email"
              value={formData.email}
              onChange={handleInputChange('email')}
              error={!!errors.email}
              helperText={errors.email}
              required
            />
          </Box>

          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr 1fr' }, gap: 3 }}>
            <TextField
              fullWidth
              label="CPF"
              value={formData.cpf}
              onChange={handleInputChange('cpf')}
              error={!!errors.cpf}
              helperText={errors.cpf}
              required
              slotProps={{
                htmlInput: { maxLength: 11 }
              }}
            />
            
            <TextField
              fullWidth
              label="RG"
              value={formData.rg}
              onChange={handleInputChange('rg')}
              error={!!errors.rg}
              helperText={errors.rg}
              required
            />
            
            <TextField
              fullWidth
              label="Telefone 1"
              value={formData.telefone1}
              onChange={handleInputChange('telefone1')}
              error={!!errors.telefone1}
              helperText={errors.telefone1}
              required
              slotProps={{
                htmlInput: { maxLength: 15 }
              }}
            />
          </Box>

          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 3 }}>
            <TextField
              fullWidth
              label="Telefone 2"
              value={formData.telefone2}
              onChange={handleInputChange('telefone2')}
              slotProps={{
                htmlInput: { maxLength: 15 }
              }}
            />
            
            <SelectCargo
              value={formData.codCargo}
              onChange={handleCargoChange}
              error={!!errors.codCargo}
              helperText={errors.codCargo}
              required
            />
          </Box>

          {/* Dados Profissionais */}
          <Typography variant="h6" sx={{ fontWeight: 'bold', color: 'primary.main', mt: 2 }}>
            Dados Profissionais
          </Typography>
          
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 3 }}>
            <DatePickerField
              label="Data de Nascimento"
              value={formData.dataNasc}
              onChange={handleDateChange('dataNasc')}
              error={!!errors.dataNasc}
              helperText={errors.dataNasc}
              required
            />

            <DatePickerField
              label="Data de Contratação"
              value={formData.dataContratacao}
              onChange={handleDateChange('dataContratacao')}
              error={!!errors.dataContratacao}
              helperText={errors.dataContratacao}
              required
            />
          </Box>

          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 3 }}>
            <DatePickerField
              label="Data de Recisão"
              value={formData.dataRecisao || ''}
              onChange={handleDateChange('dataRecisao')}
            />
            
            <Box sx={{ display: 'flex', alignItems: 'center', mt: 2 }}>
              <FormControlLabel
                control={
                  <Switch
                    checked={formData.ativo === 1}
                    onChange={handleAtivoChange}
                  />
                }
                label="Ativo"
              />
            </Box>
          </Box>

          {/* Botões */}
          <Box sx={{ display: 'flex', gap: 2, mt: 3 }}>
            <Button
              type="submit"
              variant="contained"
              startIcon={<SaveIcon />}
              disabled={saveMutation.isPending}
              sx={{ minWidth: 120 }}
            >
              {saveMutation.isPending ? 'Salvando...' : 'Salvar'}
            </Button>
            <Button
              variant="outlined"
              startIcon={<ClearIcon />}
              onClick={handleClear}
            >
              Limpar
            </Button>
            <Button
              variant="outlined"
              startIcon={<ArrowBackIcon />}
              onClick={handleBack}
            >
              Voltar
            </Button>
          </Box>
        </Box>
      </Box>
    </Paper>
  );
};

export default FuncionarioForm;
